// Global phone: starts the Twilio Device after sign-in, shows the incoming-call modal, a call bar and the dial pad
// button (SPEC §19) on every page.
// Mounted once in the app shell (web/src/app.tsx).
import '../dialer/dialer.css';
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { BusinessDetail } from '../dialer/types';
import { endpoints } from '../dialer/endpoints';
import { DISPOSITION_META, fmtPhone } from '../dialer/labels';
import { DialPad } from '../dialer/DialPad';
import { RedialConfirm } from '../dialer/RedialConfirm';
import { ConsentRecordConfirm } from '../dialer/ConsentRecordConfirm';
import { checkPending, getDialer, hangup as dialerHangup, resetDialer, subscribeDialer } from '../dialer/store';
import { fmtDuration } from '../dialer/time';
import { navigate } from '../app';
import { timeAgo } from '../components/ui';
import { useExternal, useNow } from '../components/ui-extra';
import { firstName } from '../dialer/script';
import { useMe } from '../me';
import { Keypad } from './Keypad';
import { phone, type PhoneState } from './manager';

const subscribePhone = (fn: () => void) => phone.subscribe(fn);
const getPhone = () => phone.getState();

export function usePhone(): PhoneState {
  return useExternal(subscribePhone, getPhone);
}

export function PhoneProvider(props: { children: ComponentChildren }) {
  useEffect(() => {
    void phone.start();
    void checkPending(); // SPEC §8.2: an unfinished call is surfaced on load, whatever page the setter lands on
    const online = () => { if (phone.getState().status === 'offline' && !phone.hasCall()) void phone.restart(); };
    window.addEventListener('online', online);
    return () => {
      window.removeEventListener('online', online);
      resetDialer();
      phone.stopWhenIdle();
    };
  }, []);
  return (
    <>
      {props.children}
      <GlobalCallBar />
      <DialPad />
      {/* "Call this number again?" (PJ 2026-10-20): one dialog for the dialer, the dial pad and every page they open from. */}
      <RedialConfirm />
      {/* "Record the rest of this call?" (PJ 2026-10-20): ask first; only "They said yes, start" records. */}
      <ConsentRecordConfirm />
      <IncomingCallModal />
    </>
  );
}

function IncomingCallModal() {
  const me = useMe();
  const s = usePhone();
  const inc = s.incoming;
  const [detail, setDetail] = useState<BusinessDetail | null>(null);
  const [lookupFailed, setLookupFailed] = useState(false);
  useEffect(() => {
    setDetail(null);
    setLookupFailed(false);
    if (!inc?.businessId) return;
    let live = true;
    endpoints.business(inc.businessId).then((d) => { if (live) setDetail(d); }, () => { if (live) setLookupFailed(true); });
    return () => { live = false; };
  }, [inc?.businessId, inc?.receivedAt]);
  if (!inc) return null;
  const last = detail?.history?.[0];
  return (
    <div class="vd-incoming-backdrop">
      <div class="card vd-incoming" role="alertdialog" aria-label="Incoming call">
        <div class="eyebrow">Incoming call</div>
        <div class="vd-incoming-number mono">{fmtPhone(inc.from)}</div>
        {detail ? (
          <div class="stack" style="gap:4px">
            <strong>{detail.business.name}</strong>
            <span class="muted">{[detail.business.city, detail.business.stateProv].filter(Boolean).join(', ')}{detail.business.dmName ? ` · ${detail.business.dmName}` : ''}</span>
            {last ? (
              <span class="muted">
                Last call {timeAgo(last.createdAt)}{last.userName ? ` by ${last.userName}` : ''}
                {last.disposition ? `: ${DISPOSITION_META[last.disposition]?.label ?? last.disposition}` : ''}
                {last.note ? ` · "${last.note.slice(0, 120)}"` : ''}
              </span>
            ) : <span class="muted">No earlier calls on record.</span>}
          </div>
        ) : (
          <div class="stack" style="gap:4px">
            {inc.businessName ? <strong>{inc.businessName}</strong> : null}
            <span class="muted">{inc.businessId && !lookupFailed ? 'Loading their last call…' : lookupFailed ? "Couldn't load the call history." : 'Not a business we have on file.'}</span>
          </div>
        )}
        <p class="vd-field-hint" style="margin:10px 0 0">Answer with "Valistix, this is {firstName(me.name) ?? me.name}." If you decline, they reach the voicemail box.</p>
        <div class="row" style="margin-top:14px;gap:10px">
          <button class="btn btn-primary" style="flex:1" onClick={() => phone.acceptIncoming()}>Accept</button>
          <button class="btn" style="flex:1" onClick={() => phone.rejectIncoming()}>Decline</button>
        </div>
      </div>
    </div>
  );
}

/** Call controls on every page: inbound calls always, outbound calls when the dialer screen isn't showing. */
function GlobalCallBar() {
  const s = usePhone();
  const dialer = useExternal(subscribeDialer, getDialer);
  const now = useNow(1000);
  const [keypad, setKeypad] = useState(false);
  const a = s.active;
  useEffect(() => { if (!a) setKeypad(false); }, [a]);
  if (!a) return null;
  if (a.direction === 'outbound' && dialer.dialerVisible) return null;
  const seconds = a.connectedAt ? (now - a.connectedAt) / 1000 : 0;
  const who = a.direction === 'inbound'
    ? `Inbound · ${a.businessName ? `${a.businessName} · ` : ''}${fmtPhone(a.from)}`
    : `Outbound · ${dialer.item?.business.name ?? 'call in progress'}`;
  return (
    <div class="vd-globalbar" role="region" aria-label="Current call">
      <span class="vd-globalbar-who">{who}</span>
      <span class="mono">{a.connectedAt ? fmtDuration(seconds) : 'connecting…'}</span>
      {s.warnings.length ? <span class="chip chip-warn">{s.warnings[0]}</span> : null}
      <button class="btn btn-sm" onClick={() => phone.toggleMute()}>{a.muted ? 'Unmute' : 'Mute'}</button>
      <button class="btn btn-sm" onClick={() => setKeypad(!keypad)}>Keypad</button>
      {a.direction === 'outbound' ? <button class="btn btn-sm" onClick={() => navigate('/')}>Open dialer</button> : null}
      <button class="btn btn-sm btn-danger" onClick={() => (a.direction === 'outbound' ? dialerHangup() : phone.hangup())}>Hang up</button>
      {keypad ? <div class="vd-globalbar-keypad card"><Keypad onDigit={(d) => phone.sendDigits(d)} onClose={() => setKeypad(false)} /></div> : null}
    </div>
  );
}
